/**
 * Local copy of the current user's profile, for when the server cannot be
 * reached. The server is the source of truth; consumers go through
 * `profile.service.ts`.
 */

import { useAuthStore } from '@/features/auth/store/auth.store'
import { userDisplayName } from '@/features/auth/utils/display-name'

export interface ProfileRecord {
  name: string
  email: string
  phone: string
  role: string
  location: string
  avatarUrl: string
}

const STORAGE_KEY = 'inmeet:settings:profile:v2'

/**
 * Copies written before phone and location moved to the server. Dropped, not
 * read: this file used to seed every user's phone with one employee's real
 * number, and a save wrote the whole record, seed included, so browsers still
 * hold it. Everything else in them now comes from the server.
 */
const OLD_STORAGE_KEY = 'inmeet:settings:profile'

// Empty, not sample values: the server holds the real ones, and these are only
// what shows when it cannot be reached.
const SEED: ProfileRecord = {
  name: '',
  email: '',
  phone: '',
  role: '',
  location: '',
  // Deliberately empty. This used to seed one person's Zoho photo, so every
  // user without a picture saw the same stranger's face and believed it was
  // their account's avatar. Empty means the UI falls back to initials, and a
  // real picture now comes from the server (`avatar.api.ts`).
  avatarUrl: '',
}

/**
 * Identity comes from the session, not the seed — the seed used to carry a
 * developer's real name/email, so every user saw it on the Settings profile
 * until they edited it.
 */
function seed(): ProfileRecord {
  const user = useAuthStore.getState().user
  return {
    ...SEED,
    name: userDisplayName(user, ''),
    email: user?.email ?? '',
  }
}

function read(): ProfileRecord {
  const base = seed()
  try {
    localStorage.removeItem(OLD_STORAGE_KEY)
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return base
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object') return { ...base, ...parsed }
  } catch { /* ignore */ }
  return base
}

function write(rec: ProfileRecord) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rec))
  } catch { /* ignore */ }
}

export const profileRepo = {
  async get(): Promise<ProfileRecord> {
    return read()
  },
  async update(patch: Partial<ProfileRecord>): Promise<ProfileRecord> {
    const next = { ...read(), ...patch }
    write(next)
    return next
  },
  async clear(): Promise<void> {
    try { localStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
  },
}
